import React from 'react';
import { useTenants } from '../../contexts/TenantContext';

const BUILDING_ICON =
  'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4';

interface TenantSwitcherProps {
  collapsed?: boolean;
}

export const TenantSwitcher: React.FC<TenantSwitcherProps> = ({ collapsed = false }) => {
  const { tenants, currentTenantId, currentTenant, switchTenant, switching, error } = useTenants();

  if (tenants.length <= 1) {
    return null;
  }

  const options = tenants.map(tenant => (
    <option key={tenant.id} value={tenant.id} className="text-gray-900">
      {tenant.name}
    </option>
  ));

  const onChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const tenantId = event.target.value;
    if (tenantId) void switchTenant(tenantId);
  };

  if (collapsed) {
    return (
      <div className="relative mt-1 flex justify-center">
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-brand-200">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={BUILDING_ICON} />
          </svg>
        </span>
        <select
          aria-label="Switch tenant"
          title={currentTenant?.name ?? 'Switch tenant'}
          value={currentTenantId ?? ''}
          disabled={switching}
          onChange={onChange}
          className="w-9 h-9 rounded-lg bg-white/10 border border-white/15 text-transparent cursor-pointer appearance-none disabled:opacity-60"
        >
          {!currentTenantId && (
            <option value="" disabled>
              Select tenant
            </option>
          )}
          {options}
        </select>
      </div>
    );
  }

  return (
    <div className="mt-1">
      <div className="relative">
        <svg
          className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-300 pointer-events-none"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={BUILDING_ICON} />
        </svg>
        <select
          id="tenant-switcher"
          aria-label="Switch tenant"
          value={currentTenantId ?? ''}
          disabled={switching}
          onChange={onChange}
          className="w-full appearance-none pl-7 pr-7 py-1.5 rounded-md bg-white/10 text-white text-xs font-medium border border-white/15 hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-brand-400 cursor-pointer disabled:opacity-60"
        >
          {!currentTenantId && (
            <option value="" disabled>
              Select tenant
            </option>
          )}
          {options}
        </select>
        <svg
          className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-brand-300 pointer-events-none"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      {switching && <p className="mt-1 text-[10px] text-brand-300">Switching…</p>}
      {error && <p className="mt-1 text-[10px] text-red-300">{error}</p>}
    </div>
  );
};
